import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { ProjectionKind, useAtsCandidatesDataTable } from 'generated/resources/datatable.graphql'

import DataTable from 'modules/core/components/DataTable'
import ErrorBoundary from 'modules/core/components/ErrorBoundary'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { ScoreRating } from 'modules/ats/components/ScoreRating'
import { isConsentExpired } from 'modules/ats/lib/utils/candidate'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

import { CandidateActions } from './CandidateActions'
import { CandidateFilterOptions, candidateFilters } from './CandidateFilters'
import { Highlighted } from './Highlighted'
import { JobsList } from './JobsList'
import { TableBulkActions } from './TableBulkActions'

interface Props {
  candidateFilterOptions: CandidateFilterOptions
  isConsentLimitEnabled: boolean
  isBulkActionsEnabled: boolean
}
export const CandidatesDataTable = ({
  candidateFilterOptions,
  isConsentLimitEnabled,
  isBulkActionsEnabled,
}: Props) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()

  const filters = candidateFilters(candidateFilterOptions).filter(
    (filter) => !!isConsentLimitEnabled || filter.identifier !== 'candidateSearches.consentFilter'
  )

  const source = useAtsCandidatesDataTable(
    [
      {
        identifier: 'name',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.candidate'),
        template: '{{ candidate.fullName }}',
        render: (candidate) => (
          <Highlighted candidate={candidate} fields={['full_name']} minWidth="s200">
            <>
              <Text.Normal>{candidate.fullName}</Text.Normal>
              <Highlighted
                candidate={candidate}
                fields={['email', 'phone_number', 'cvs', 'apps.cvs']}
                size="100"
                minWidth="s360"
              ></Highlighted>
            </>
          </Highlighted>
        ),
        sortBy: 'fullName',
        sticky: true,
        fixed: true,
      },
      {
        identifier: 'jobs',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.jobs'),
        template: '{{ candidate.talentPool }}',
        render: (candidate) => (
          <Box minWidth="s240">
            <JobsList candidate={candidate} maximum={2} />
          </Box>
        ),
        colSpan: 3,
      },
      {
        identifier: 'source',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.source'),
        template: '{{ candidate.candidateSource.name }}',
        render: (candidate) => (
          <Box minWidth="s200">
            <Tag type="grey" inline size="normal">
              {candidate.candidateSource && candidate.medium
                ? `${candidate.medium} (${getSourceLabel(candidate.candidateSource.name)})`
                : getSourceLabel(candidate.candidateSource?.name)}
            </Tag>
          </Box>
        ),
      },
      {
        identifier: 'ratingAverage',
        sortBy: 'ratingAverage',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.rating'),
        template: '{{ candidate.ratingAverage }}',
        justifyContent: 'center',
        render: (candidate) => (
          <Box alignItems="center" minWidth="s64">
            <ScoreRating
              rating={candidate.ratingAverage && Math.floor(candidate.ratingAverage)}
              showEmpty={false}
            />
          </Box>
        ),
      },
      {
        identifier: 'appliedOn',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.applied_on'),
        template: '{{ candidate.createdAt }}',
        render: (candidate) => (
          <Box minWidth="s120">
            <Text.Normal>
              {convertDate(candidate.createdAt, 'backend_datetime', 'ui_abbreviated_month_year')}
            </Text.Normal>
          </Box>
        ),
        sortBy: 'createdAt',
      },
      {
        hidden: !isConsentLimitEnabled,
        identifier: 'consent',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.consent'),
        template: '{{ candidate.consentExpirationDate }}',
        render: (candidate) => (
          <Box key={`consent_${candidate.id}`} minWidth="s140">
            <Text.Normal
              color={
                isConsentExpired(candidate.consentExpirationDate) ? 'radicalRed1200' : 'grey800'
              }
            >
              {!candidate.consentExpirationDate
                ? i18n.t('ats.candidates.table_body.consent_pending')
                : convertDate(
                    candidate.consentExpirationDate,
                    'backend_datetime',
                    'ui_abbreviated_month_year'
                  )}
            </Text.Normal>
          </Box>
        ),
      },
      {
        identifier: 'phone',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.phone'),
        template: '{{ candidate.phone_number }}',
        render: (candidate) => (
          <Highlighted candidate={candidate} fields={['phone_number']} minWidth="s96">
            <Text.Normal>{candidate.phoneNumber}</Text.Normal>
          </Highlighted>
        ),
      },
      {
        identifier: 'email',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.email'),
        template: '{{ candidate.email }}',
        render: (candidate) => (
          <Highlighted candidate={candidate} fields={['email']} minWidth="s200">
            <Text.Normal ellipsis>{candidate.email}</Text.Normal>
          </Highlighted>
        ),
      },
      {
        identifier: 'tags',
        kind: ProjectionKind.Custom,
        title: i18n.t('ats.candidates.table_headers.tags'),
        template: '',
        render: (candidate) => (
          <Box gap="s4" flexDirection="row" flexWrap="wrap" minWidth="s240">
            {candidate.tags.map((tag) => (
              <Tag type="champagne" key={`status_${candidate.id}_${tag.id}`} inline size="small">
                {tag.name}
              </Tag>
            ))}
          </Box>
        ),
      },
      {
        identifier: 'actions',
        kind: ProjectionKind.Decorative,
        render: (candidate) => (
          <CandidateActions candidate={candidate} key={`actions_${candidate.id}`} />
        ),
        width: 100,
        sticky: true,
        fixed: true,
      },
    ],
    { variables: { search: debouncedSearch } }
  )

  return (
    <ErrorBoundary>
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 10,
        }}
        sort={{
          path: 'score',
          direction: 'desc',
        }}
        filters={filters}
        persistFilters
        enableClearFilters
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            actions={<TableBulkActions />}
            search={{
              placeholder: i18n.t('common.search_by'),
              value: search,
              onChange: onSearch,
            }}
          />
          <DataTable.Table autoWidth selectable={isBulkActionsEnabled ? () => true : undefined} />
        </Box>
      </DataTable.Provider>
    </ErrorBoundary>
  )
}
